import { createContext, useContext, useEffect, useState } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from './supabase';
import { setUserContext, clearUserContext, captureException, addBreadcrumb } from './sentry';

interface AuthContextType {
  session: Session | null;
  user: User | null;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (password: string) => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);

      // Set Sentry user context
      if (session?.user) {
        setUserContext(session.user.id, session.user.email);
        addBreadcrumb('User session loaded', 'auth', { userId: session.user.id });
      }

      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user ?? null);

      // Update Sentry user context on auth changes
      if (session?.user) {
        setUserContext(session.user.id, session.user.email);
        addBreadcrumb(`Auth event: ${event}`, 'auth', { userId: session.user.id });
      } else {
        clearUserContext();
        addBreadcrumb(`Auth event: ${event}`, 'auth');
      }

      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      addBreadcrumb('Sign in attempt', 'auth', { email });
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      addBreadcrumb('Sign in successful', 'auth', { email });
    } catch (error) {
      captureException(error as Error, { action: 'signIn', email });
      throw error;
    }
  };

  const signUp = async (email: string, password: string) => {
    try {
      addBreadcrumb('Sign up attempt', 'auth', { email });
      const { error } = await supabase.auth.signUp({ email, password });
      if (error) throw error;
      addBreadcrumb('Sign up successful', 'auth', { email });
    } catch (error) {
      captureException(error as Error, { action: 'signUp', email });
      throw error;
    }
  };

  const signInWithGoogle = async () => {
    try {
      addBreadcrumb('Google sign in attempt', 'auth');
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/`
        }
      });
      if (error) throw error;
    } catch (error) {
      captureException(error as Error, { action: 'signInWithGoogle' });
      throw error;
    }
  };

  const signOut = async () => {
    try {
      addBreadcrumb('Sign out attempt', 'auth');
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      clearUserContext();
      addBreadcrumb('Sign out successful', 'auth');
    } catch (error) {
      captureException(error as Error, { action: 'signOut' });
      throw error;
    }
  };

  const resetPassword = async (email: string) => {
    // Create a custom reset URL that points to our app
    const resetUrl = `${window.location.origin}/reset-password`;
    
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: resetUrl,
    });
    
    if (error) throw error;
  };

  const updatePassword = async (password: string) => {
    const { error } = await supabase.auth.updateUser({
      password: password,
    });
    
    if (error) throw error;
  };

  return (
    <AuthContext.Provider value={{ 
      session, 
      user, 
      signIn, 
      signUp,
      signInWithGoogle,
      signOut, 
      resetPassword, 
      updatePassword,
      loading 
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}